import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Modal, Input, Spin } from 'Components';
import { Activity } from 'Services/Api/Activities/Types';
import styles from './SelectCourseModal.module.scss';

interface SelectCourseModalProps {
	visible: boolean;
	activities: Activity[];
	loading?: boolean;
	selectedId?: number | null;
	onSelect: (activity: Activity) => void;
	onCancel: () => void;
}

const SelectCourseModal: React.FC<SelectCourseModalProps> = ({
	visible,
	activities,
	loading = false,
	selectedId,
	onSelect,
	onCancel
}) => {
	const [search, setSearch] = useState('');
	const { t } = useTranslation();

	const filtered = useMemo(() => {
		const query = search.trim().toLowerCase();
		if (!query) return activities;
		return activities.filter((activity) =>
			activity.name.toLowerCase().includes(query)
		);
	}, [activities, search]);

	return (
		<Modal
			visible={visible}
			onCancel={onCancel}
			footer={null}
			size="sm"
			centered
			title={t('learning.selectCourseToEdit.title')}
		>
			<div className={styles.wrapper}>
				<Input
					className={styles.search}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					placeholder={t('learning.selectCourseToEdit.searchPlaceholder')}
				/>

				<div className={styles.list}>
					{loading ? (
						<div className={styles.state}>
							<Spin />
						</div>
					) : filtered.length === 0 ? (
						<div className={styles.state}>
							{t('learning.selectCourseToEdit.noCoursesFound')}
						</div>
					) : (
						filtered.map((activity) => (
							<div
								key={activity.id}
								role="button"
								tabIndex={0}
								className={classNames(styles.item, {
									[styles['item--selected']]:
										activity.id === selectedId
								})}
								onClick={() => onSelect(activity)}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										onSelect(activity);
									}
								}}
							>
								{activity.name}
							</div>
						))
					)}
				</div>
			</div>
		</Modal>
	);
};

export default SelectCourseModal;